import { useChartColors } from '../lib/accessibility'
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, X, CheckCircle2, Gauge } from 'lucide-react'
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts'
import { api } from '../lib/api'

const C = {
  bg: 'var(--color-bg)', bgRaised: 'var(--color-bg-card)', bgCard: '#1A2212',
  border: 'var(--color-border)', amber: 'var(--color-primary)', green: '#2D6A2F',
  text: 'var(--color-text)', textMuted: 'var(--color-text-muted)', textFaint: 'var(--color-text-faint)',
  red: '#F87171', blue: '#60A5FA',
}

const SENSOR_ICONS: Record<string, string> = {
  moisture: '💧', temperature: '🌡️', humidity: '💨', ph: '⚗️', ec: '⚡', custom: '📡',
}

interface Sensor {
  id: string; name: string; sensor_type: string; unit: string
  brand?: string; model?: string; serial_number?: string; field_id?: string
  current_value?: number; last_reading_at?: string; status: 'active' | 'inactive' | 'error'
  installed_at?: string
}

interface SensorReading {
  id: string; value: number; recorded_at: string
}

const inputStyle: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box',
  background: C.bg, border: `1px solid ${C.border}`,
  borderRadius: 8, padding: '8px 12px',
  color: C.text, fontSize: 13, outline: 'none',
}
const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: 12, fontWeight: 500, color: C.textMuted, marginBottom: 5,
}

function AddSensorModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient()
  const [form, setForm] = useState({
    name: '', sensor_type: 'moisture', unit: '%', brand: '', model: '',
    serial_number: '', field_id: '', resource_type: 'field',
    installed_at: new Date().toISOString().slice(0, 10),
  })
  const [success, setSuccess] = useState(false)
  const mutation = useMutation({
    mutationFn: (d: Record<string, unknown>) => api.post('/sensors', d),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['sensors'] }); setSuccess(true); setTimeout(onClose, 1200) },
  })
  function submit(e: React.FormEvent) { e.preventDefault(); mutation.mutate({ ...form }) }
  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }))
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div style={{ background: C.bgRaised, border: `1px solid ${C.border}`, borderRadius: 14, width: 480, maxWidth: '95vw', padding: 24, maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.text }}>Add Sensor</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textFaint }}><X size={18} /></button>
        </div>
        {success ? (
          <div style={{ textAlign: 'center', padding: '24px 0', color: '#4ADE80' }}><CheckCircle2 size={36} style={{ marginBottom: 10 }} /><div style={{ fontSize: 15, fontWeight: 600 }}>Sensor added!</div></div>
        ) : (
          <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 2 }}><label style={labelStyle}>Name *</label><input required style={inputStyle} value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Grove Sector A Moisture" /></div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Type</label>
                <select style={inputStyle} value={form.sensor_type} onChange={e => set('sensor_type', e.target.value)}>
                  {['moisture','temperature','humidity','ph','ec','custom'].map(t => <option key={t} value={t}>{SENSOR_ICONS[t]} {t}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}><label style={labelStyle}>Unit</label><input style={inputStyle} value={form.unit} onChange={e => set('unit', e.target.value)} placeholder="%, °F, pH, mS/cm" /></div>
              <div style={{ flex: 1 }}><label style={labelStyle}>Brand</label><input style={inputStyle} value={form.brand} onChange={e => set('brand', e.target.value)} /></div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}><label style={labelStyle}>Model</label><input style={inputStyle} value={form.model} onChange={e => set('model', e.target.value)} /></div>
              <div style={{ flex: 1 }}><label style={labelStyle}>Serial #</label><input style={inputStyle} value={form.serial_number} onChange={e => set('serial_number', e.target.value)} /></div>
            </div>
            <div><label style={labelStyle}>Field / Zone ID</label><input style={inputStyle} value={form.field_id} onChange={e => set('field_id', e.target.value)} /></div>
            <div><label style={labelStyle}>Installed Date</label><input type="date" style={inputStyle} value={form.installed_at} onChange={e => set('installed_at', e.target.value)} /></div>
            {mutation.isError && <div style={{ fontSize: 12, color: C.red, padding: '8px 12px', background: C.red + '11', borderRadius: 8 }}>Failed to save.</div>}
            <button type="submit" disabled={mutation.isPending} style={{ padding: '10px 0', borderRadius: 8, background: C.green, border: 'none', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
              {mutation.isPending ? 'Saving…' : 'Add Sensor'}
            </button>
          </form>
        )}
      </div>
    </div>
  )
}

function LogReadingModal({ sensorId, onClose }: { sensorId: string; onClose: () => void }) {
  const qc = useQueryClient()
  const [form, setForm] = useState({ value: '', recorded_at: new Date().toISOString().slice(0, 16) })
  const [success, setSuccess] = useState(false)
  const mutation = useMutation({
    mutationFn: (d: Record<string, unknown>) => api.post(`/sensors/${sensorId}/readings`, d),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['sensor-readings', sensorId] }); setSuccess(true); setTimeout(onClose, 1200) },
  })
  function submit(e: React.FormEvent) {
    e.preventDefault()
    mutation.mutate({ value: parseFloat(form.value), recorded_at: new Date(form.recorded_at).toISOString() })
  }
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div style={{ background: C.bgRaised, border: `1px solid ${C.border}`, borderRadius: 14, width: 360, maxWidth: '95vw', padding: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.text }}>Log Reading</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textFaint }}><X size={18} /></button>
        </div>
        {success ? (
          <div style={{ textAlign: 'center', padding: '24px 0', color: '#4ADE80' }}><CheckCircle2 size={36} style={{ marginBottom: 10 }} /><div>Reading logged!</div></div>
        ) : (
          <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div><label style={labelStyle}>Value *</label><input required type="number" step="any" style={inputStyle} value={form.value} onChange={e => setForm(f => ({ ...f, value: e.target.value }))} /></div>
            <div><label style={labelStyle}>Timestamp</label><input type="datetime-local" style={inputStyle} value={form.recorded_at} onChange={e => setForm(f => ({ ...f, recorded_at: e.target.value }))} /></div>
            {mutation.isError && <div style={{ fontSize: 12, color: C.red, padding: '8px 12px', background: C.red + '11', borderRadius: 8 }}>Failed to save.</div>}
            <button type="submit" disabled={mutation.isPending} style={{ padding: '10px 0', borderRadius: 8, background: C.green, border: 'none', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
              {mutation.isPending ? 'Saving…' : 'Log Reading'}
            </button>
          </form>
        )}
      </div>
    </div>
  )
}

function SensorCard({ sensor }: { sensor: Sensor }) {
  const chartC = useChartColors()
  const [expanded, setExpanded] = useState(false)
  const [showLog, setShowLog] = useState(false)
  const { data: readings = [] } = useQuery<SensorReading[]>({
    queryKey: ['sensor-readings', sensor.id],
    queryFn: () => api.get(`/sensors/${sensor.id}/readings`, { params: { hours: 24 } }).then(r => r.data?.readings ?? r.data ?? []),
    enabled: expanded,
  })

  const isActive = sensor.status === 'active'
  const chartData = readings.map(r => ({ time: new Date(r.recorded_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }), value: r.value }))

  return (
    <>
      {showLog && <LogReadingModal sensorId={sensor.id} onClose={() => setShowLog(false)} />}
      <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden' }}>
        <div style={{ padding: '16px 18px', cursor: 'pointer' }} onClick={() => setExpanded(!expanded)}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 22 }}>{SENSOR_ICONS[sensor.sensor_type] ?? '📡'}</span>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{sensor.name}</div>
                <div style={{ fontSize: 11, color: C.textMuted }}>{sensor.brand} {sensor.model}</div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ width: 7, height: 7, borderRadius: '50%', background: isActive ? '#4ADE80' : C.textFaint }} />
              <span style={{ fontSize: 11, color: isActive ? '#4ADE80' : C.textFaint }}>{isActive ? 'Active' : 'Inactive'}</span>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: 28, fontWeight: 700, color: C.text, lineHeight: 1 }}>
                {sensor.current_value !== undefined ? sensor.current_value : '—'}
                <span style={{ fontSize: 14, color: C.textMuted, marginLeft: 4 }}>{sensor.unit}</span>
              </div>
              <div style={{ fontSize: 11, color: C.textFaint, marginTop: 4 }}>
                {sensor.last_reading_at ? `Last: ${new Date(sensor.last_reading_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}` : 'No readings yet'}
              </div>
            </div>
            <button
              onClick={e => { e.stopPropagation(); setShowLog(true) }}
              style={{ padding: '5px 12px', borderRadius: 7, background: 'transparent', border: `1px solid ${C.border}`, color: C.textMuted, fontSize: 11, cursor: 'pointer' }}
            >
              Log Reading
            </button>
          </div>
        </div>
        {expanded && (
          <div style={{ padding: '0 18px 16px', borderTop: `1px solid ${C.border}` }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.textFaint, textTransform: 'uppercase', letterSpacing: '0.06em', paddingTop: 12, marginBottom: 10 }}>Last 24 Hours</div>
            {readings.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '24px 0', color: C.textFaint, fontSize: 12 }}>No readings in the last 24 hours</div>
            ) : (
              <ResponsiveContainer width="100%" height={120}>
                <LineChart data={chartData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                  <XAxis dataKey="time" tick={{ fontSize: 10, fill: chartC.faint }} tickLine={false} axisLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: chartC.faint }} tickLine={false} axisLine={false} />
                  <Tooltip
                    contentStyle={{ background: C.bgRaised, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 12 }}
                    labelStyle={{ color: C.textMuted }}
                    itemStyle={{ color: C.amber }}
                  />
                  <Line type="monotone" dataKey="value" stroke={chartC.accent} strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        )}
      </div>
    </>
  )
}

export function SensorsPage() {
  const [showAdd, setShowAdd] = useState(false)

  const { data: sensors = [], isLoading, isError, refetch } = useQuery<Sensor[]>({
    queryKey: ['sensors'],
    queryFn: () => api.get('/sensors').then(r => r.data?.sensors ?? r.data ?? []),
  })

  return (
    <>
      {showAdd && <AddSensorModal onClose={() => setShowAdd(false)} />}
      <div style={{ padding: '28px 32px', minHeight: '100%', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: C.text }}>Sensors</h1>
            <p style={{ margin: '3px 0 0', fontSize: 13, color: C.textMuted }}>Field sensors and environmental monitoring</p>
          </div>
          <button onClick={() => setShowAdd(true)} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 18px', borderRadius: 9, background: C.green, border: 'none', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
            <Plus size={16} /> Add Sensor
          </button>
        </div>

        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: C.textFaint }}>Loading sensors…</div>
        ) : isError ? (
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <p style={{ color: C.red, marginBottom: 12 }}>Failed to load sensors</p>
            <button onClick={() => refetch()} style={{ padding: '8px 16px', borderRadius: 8, background: C.bgCard, border: `1px solid ${C.border}`, color: C.text, cursor: 'pointer' }}>Retry</button>
          </div>
        ) : sensors.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '80px 0' }}>
            <Gauge size={40} style={{ color: C.textFaint, opacity: 0.4, marginBottom: 12 }} />
            <p style={{ margin: 0, color: C.textFaint }}>No sensors configured</p>
            <p style={{ margin: '6px 0 0', fontSize: 12, color: C.textFaint }}>Click "Add Sensor" to start monitoring soil moisture, temperature, and more</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
            {sensors.map(s => <SensorCard key={s.id} sensor={s} />)}
          </div>
        )}
      </div>
    </>
  )
}
